import React, {useEffect, useRef, useState} from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import {AppToaster} from './components/ui/AppToaster';
import Layout from './components/Layout';

import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import {ThemeProvider} from './components/ThemeProvider';
import {useAuthStore} from './store/authStore';
import {useBrandingStore} from './store/brandingStore';
import { useForceBrandingUpdate } from './hooks/useForceBrandingUpdate';
// Pages
import Login from './pages/Login';
import VacancyList from './pages/VacancyList';
import VacancyViewPage from './pages/VacancyViewPage';
import InterviewsList from './pages/InterviewsList';
import CreateVacancyPage from './pages/CreateVacancyPage';
import EditVacancyPage from './pages/EditVacancyPage';
import VacancyDataEditPage from './pages/VacancyDataEditPage';
import InterviewQuestionsEditPage from './pages/InterviewQuestionsEditPage';
import StatisticsPage from './components/StatisticsPage';
import RelevanterPage from './pages/RelevanterPage';
import HHResponsesPage from './pages/HHResponsesPage';
import MessengerPage from './pages/MessengerPage';
import MailingsPage from './pages/MailingsPage';
import InterviewsPage from './pages/InterviewsPage';

import {PersonalInfoPage} from './components/PersonalInfoPage';

import {TeamManagementPage} from './components/TeamManagementPage';
import {CreditsLimitsContent} from './components/CreditsLimitsContent';
import {BrandingPage} from './components/BrandingPage';
import {QuestionsPage} from './components/QuestionsPage';

import SessionPage from './pages/SessionPage';
import ApplicantDetailPage from './pages/ApplicantDetailPage';
import VacancyPage from './pages/VacancyPage';
import api from './relevanter/services/api';
import AtsPage from './ats/components/AtsPage';
import { isAtsAvailable } from './ats/atsConfig';
import toast from 'react-hot-toast';
import {
    getHhOAuthOutcomeNotification,
    handleHhOAuthCallback,
} from './relevanter/services/hhOAuthCallback';

const AtsRoute: React.FC = () => {
    const { companyData, loadCompanyData } = useBrandingStore();
    const [companyChecked, setCompanyChecked] = useState(Boolean(companyData));

    useEffect(() => {
        let cancelled = false;
        if (companyData) {
            setCompanyChecked(true);
            return () => {
                cancelled = true;
            };
        }
        setCompanyChecked(false);
        loadCompanyData()
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setCompanyChecked(true);
            });

        return () => {
            cancelled = true;
        };
    }, [companyData, loadCompanyData]);

    if (!companyChecked) {
        return <div className="min-h-screen bg-[#F5F4EF] p-8 text-[#525866]">Загрузка ATS...</div>;
    }

    return isAtsAvailable(companyData) ? <AtsPage/> : <Navigate to="/recruiter" replace/>;
};

// --- Новый компонент для разделения инициализации сессии ---
export const SessionInitializer: React.FC = () => {
    const isAuth = useAuthStore(state => state.isAuth);
    const {initFromStorage, data: brandingData} = useBrandingStore();
    const forceUpdate = useForceBrandingUpdate();
    const location = useLocation();
    const navigate = useNavigate();
    const lastProcessedCallbackRef = useRef<string | null>(null);

    useEffect(() => {
        // Инициализируем брендинг из localStorage при старте приложения
        initFromStorage();
        
        // Принудительно обновляем CSS-переменные после инициализации
        setTimeout(() => {
            forceUpdate();
        }, 100);
    }, [initFromStorage, forceUpdate]);

    // Сессия уже инициализируется в authStore при создании

    // Устанавливаем CSS-переменные бренда для использования в стилях (focus и т.п.)
    useEffect(() => {
        if (brandingData) {
            forceUpdate();
        }
    }, [brandingData, forceUpdate]);

    // Единственный обработчик callback HH OAuth для любого маршрута.
    useEffect(() => {
        const params = new URLSearchParams(location.search || '');
        const callbackKey = params.get('code') || params.get('error');
        if (!callbackKey || lastProcessedCallbackRef.current === callbackKey) return;
        lastProcessedCallbackRef.current = callbackKey;

        void handleHhOAuthCallback({
            search: location.search,
            pathname: window.location.pathname,
            hash: window.location.hash,
            isAuthenticated: isAuth,
            postCode: async code => (await api.post('/auth/hh/token', { code })).data,
            getStatus: async () => (await api.get('/auth/hh/status')).data,
            replaceUrl: url => {
              window.history.replaceState({}, '', url);
              window.dispatchEvent(new Event('hh-oauth-query-cleared'));
            },
        }).then(result => {
            if (!result) return;
            const notification = getHhOAuthOutcomeNotification(result.outcome);
            toast[notification.type](notification.message);

            if (result.outcome === 'session_expired') {
                navigate('/login');
            } else {
                navigate('/recruiter/search?admin=settings-hh');
            }
        });
    }, [location.search, navigate, isAuth]);
    return null;
};

function App() {
    return (
        <ThemeProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                {/* Инициализация сессии для CRM */}
                <SessionInitializer/>
                {/* Глобальный контейнер для toast-уведомлений */}
                <AppToaster/>
                <Routes>
                    {/* Главная страница с редиректом по роли */}
                    <Route path="/" element={<RoleBasedRedirect/>}/>

                    {/* Публичные страницы (без Layout) */}
                    <Route path="/login" element={<Login/>}/>
                    <Route path="/recruiter/login" element={<Login/>}/>

                    <Route path="/session/:sessionId" element={<SessionPage/>}/>
                    <Route path="/vacancy/:positionId" element={<VacancyPage/>}/>

                    <Route path="/ats" element={<ProtectedRoute><Layout/></ProtectedRoute>}>
                        <Route index element={<AtsRoute/>}/>
                    </Route>

                    {/* Рекрутерский интерфейс (простой дизайн) */}
                    <Route path="/recruiter" element={<ProtectedRoute><Layout/></ProtectedRoute>}>
                        <Route index element={<StatisticsPage/>}/>
                        <Route path="vacancy/:id" element={<VacancyViewPage/>}/>
                        <Route path="vacancy" element={<VacancyList/>}/>
                        <Route path="vacancy/create" element={<CreateVacancyPage/>}/>
                        <Route path="vacancy/edit/:id" element={<EditVacancyPage/>}/>
                        <Route path="vacancy/edit-data/:id" element={<VacancyDataEditPage/>}/>
                        <Route path="vacancy/edit-questions/:id" element={<InterviewQuestionsEditPage/>}/>
                        <Route path="search" element={<RelevanterPage/>}/>
                        <Route path="search/hh-auth" element={<Navigate to="/recruiter/search?admin=settings-hh" replace/>}/>
                        <Route path="hh-responses" element={<HHResponsesPage/>}/>
                        <Route path="mailings" element={<MailingsPage/>}/>
                        <Route path="messenger" element={<MessengerPage/>}/>
                        <Route path="interviews" element={<InterviewsPage/>}/>
                        <Route
                            path="ats"
                            element={<Navigate to="/ats" replace/>}
                        />

                        {/* Отдельная страница для AI-скрининга */}
                        <Route path="ai-screening" element={<Navigate to="/recruiter/interviews?mode=ai-screening" replace/>}/>

                        <Route path="reports" element={<StatisticsPage/>}/>
                        <Route path="stats" element={<StatisticsPage/>}/>
                        <Route path="account" element={<PersonalInfoPage/>}/>
                        <Route path="control" element={<TeamManagementPage/>}/>

                        <Route path="limits" element={<CreditsLimitsContent/>}/>
                        <Route path="branding" element={<BrandingPage/>}/>
                        <Route path="learn" element={<BrandingPage/>}/>
                        <Route path="questions" element={<QuestionsPage/>}/>

                        {/* Applicant detail page */}
                        <Route path="applicant/:id" element={<ApplicantDetailPage/>}/>
                    </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace/>}/>
                </Routes>
            </Router>
        </ThemeProvider>
    );
}

export default App;
